import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

const colors = [
    [255, 255, 255],
    [151, 255, 254],
    [125, 69, 150],
];

const gradientSpeed = 0.007;

const LinearGradientAi = ({ className }: { className?: string }) => {
    const [colorIndices, setColorIndices] = useState([0, 1, 2]);
    const [step, setStep] = useState(0);
    const gradientRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const updateGradient = () => {
            const c0_0 = colors[colorIndices[0]];
            const c0_1 = colors[colorIndices[1]];
            const c1_0 = colors[colorIndices[1]];
            const c1_1 = colors[colorIndices[2]];

            const istep = 1 - step;
            const r1 = Math.round(istep * c0_0[0] + step * c0_1[0]);
            const g1 = Math.round(istep * c0_0[1] + step * c0_1[1]);
            const b1 = Math.round(istep * c0_0[2] + step * c0_1[2]);
            const color1 = `rgb(${r1},${g1},${b1})`;

            const r2 = Math.round(istep * c1_0[0] + step * c1_1[0]);
            const g2 = Math.round(istep * c1_0[1] + step * c1_1[1]);
            const b2 = Math.round(istep * c1_0[2] + step * c1_1[2]);
            const color2 = `rgb(${r2},${g2},${b2})`;

            if (gradientRef.current)
                gradientRef.current.style.background = `linear-gradient(to right, ${color1}, ${color2})`;

            setStep(prevStep => {
                const newStep = prevStep + gradientSpeed;
                if (newStep >= 1) {
                    setColorIndices(prevIndices => {
                        const newIndices = [...prevIndices];
                        newIndices[0] = newIndices[1];
                        newIndices[1] = newIndices[2];
                        newIndices[2] = (newIndices[2] + Math.floor(1 + Math.random() * (colors.length - 1))) % colors.length;
                        return newIndices;
                    });
                    return newStep % 1;
                }
                return newStep;
            });
        };

        const intervalId = setInterval(updateGradient, 10);
        return () => clearInterval(intervalId);
    }, [colorIndices, step]);

    return (
        <div className={clsx(className, 'relative w-fit', 'md:scale-[1] scale-[0.7]')}>
            <svg width="0" height="0" viewBox="0 0 121 124" fill="none" xmlns="http://www.w3.org/2000/svg">
                <clipPath id="clip-star">
                    <path d="M113.021 50.3863C115.803 42.0035 114.845 32.8204 110.396 25.1954C103.705 13.5001 90.2544 7.48322 77.1178 10.3147C71.2738 3.70535 62.8777 -0.0532887 54.0753 0.000571002C40.6475 -0.030206 28.7334 8.6489 24.6023 21.4752C15.9762 23.2487 8.5304 28.6693 4.17327 36.352C-2.56745 48.0165 -1.03077 62.7202 7.97474 72.7227C5.19261 81.1056 6.15064 90.2887 10.5997 97.9137C17.2906 109.609 30.7414 115.626 43.878 112.794C49.7181 119.404 58.1181 123.162 66.9205 123.105C80.356 123.139 92.2739 114.452 96.4049 101.615C105.031 99.8411 112.477 94.4205 116.834 86.7378C123.567 75.0733 122.027 60.3812 113.025 50.3786L113.021 50.3863ZM66.9282 115.06C61.5517 115.068 56.3439 113.179 52.2167 109.721C52.4044 109.62 52.7302 109.44 52.9409 109.309L77.3593 95.1515C78.6085 94.4397 79.375 93.1048 79.3673 91.6621V57.1034L89.6872 63.0857C89.7984 63.1395 89.8712 63.2473 89.8865 63.3704V91.9891C89.8712 104.715 79.6049 115.033 66.9282 115.06ZM17.5551 93.8896C14.8611 89.2192 13.8915 83.7447 14.8151 78.4319C14.9952 78.5396 15.3133 78.7358 15.5394 78.8666L39.9577 93.024C41.1955 93.7511 42.7283 93.7511 43.9699 93.024L73.7801 75.7427V87.7073C73.7877 87.8304 73.7302 87.9496 73.6344 88.0266L48.9517 102.334C37.9573 108.689 23.9164 104.912 17.5589 93.8896H17.5551ZM11.1286 40.38C13.8111 35.7019 18.0456 32.124 23.0887 30.2659C23.0887 30.4775 23.0772 30.8506 23.0772 31.1123V59.4309C23.0695 60.8697 23.8359 62.2047 25.0814 62.9164L54.8915 80.1938L44.5716 86.1761C44.4681 86.2454 44.3378 86.2569 44.2228 86.2069L19.5363 71.8879C8.56489 65.5094 4.80173 51.4174 11.1247 40.3838L11.1286 40.38ZM95.9183 60.1888L66.1081 42.9075L76.4281 36.9291C76.5315 36.8599 76.6618 36.8483 76.7768 36.8983L101.463 51.2058C112.454 57.5805 116.221 71.6955 109.871 82.7291C107.185 87.3995 102.954 90.9773 97.9148 92.8393V63.6743C97.9263 62.2355 97.1637 60.9044 95.9221 60.1888H95.9183ZM106.188 44.6695C106.008 44.5579 105.69 44.3656 105.464 44.2348L81.0458 30.0774C79.808 29.3503 78.2751 29.3503 77.0335 30.0774L47.2234 47.3587V35.3941C47.2157 35.271 47.2732 35.1517 47.369 35.0748L72.0518 20.7789C83.0461 14.4119 97.1024 18.2013 103.441 29.2426C106.119 33.9053 107.089 39.3643 106.181 44.6695H106.188ZM41.6132 65.9941L31.2894 60.0118C31.1783 59.958 31.1055 59.8503 31.0901 59.7271V31.1084C31.0978 18.3667 41.3947 8.04106 54.0868 8.04875C59.4556 8.04875 64.6519 9.94153 68.7791 13.3886C68.5914 13.4886 68.2695 13.6694 68.0549 13.8002L43.6365 27.9576C42.3873 28.6693 41.6208 30.0004 41.6285 31.4431L41.6132 65.9864V65.9941ZM47.2196 53.8603L60.4979 46.1622L73.7762 53.8564V69.2488L60.4979 76.943L47.2196 69.2488V53.8603Z" fill="white" />
                </clipPath>
            </svg>
            <div
                ref={gradientRef}
                className='w-[121px] h-[123px]'
                style={{
                    clipPath: 'url(#clip-star)'
                }}
            />
        </div>
    );
};

export default LinearGradientAi;